import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DEFAULT_BEFORE_PARAM = 'before';
const DEFAULT_AFTER_PARAM = 'after';

export type UseAdvancedQueryParamParams<T> = {
  paramName: string;
  allowedValues?: T[];
  readValidator?: (value: unknown) => value is T;
  parse?: (value: string) => NonNullable<T>;
  format?: (value: NonNullable<T>) => string;
  defaultValue: T;
  beforeParamName?: string;
  afterParamName?: string;
};

export const useAdvancedQueryParam = <T>({
  paramName,
  allowedValues = [],
  readValidator,
  parse,
  format,
  defaultValue,
  beforeParamName = DEFAULT_BEFORE_PARAM,
  afterParamName = DEFAULT_AFTER_PARAM,
}: UseAdvancedQueryParamParams<T>): [T, (value: T) => void] => {
  const navigate = useNavigate();
  const location = useLocation();

  const defaultReadValidator = useCallback(
    (value: unknown): value is T => {
      if (allowedValues.length === 0) {
        return true;
      }

      return allowedValues.includes(value as T);
    },
    [allowedValues],
  );
  const validator = readValidator ?? defaultReadValidator;
  const searchParams = new URLSearchParams(window.location.search);
  const queryParam = searchParams.get(paramName);
  const value =
    queryParam && validator(queryParam)
      ? parse
        ? parse(queryParam)
        : (queryParam as T)
      : defaultValue;

  const setValue = (nextValue: T) => {
    const nextSearchParams = new URLSearchParams(window.location.search);

    if (
      nextValue !== null &&
      nextValue !== undefined &&
      nextValue !== '' &&
      nextValue !== defaultValue
    ) {
      const valueToSave = format ? format(nextValue as NonNullable<T>) : nextValue.toString();
      nextSearchParams.set(paramName, valueToSave);
    } else {
      nextSearchParams.delete(paramName);
    }

    nextSearchParams.delete(beforeParamName);
    nextSearchParams.delete(afterParamName);
    navigate({ search: nextSearchParams.toString() }, { state: location.state });
  };

  return [value, setValue];
};
